import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, ValidatorFn, Validators } from '@angular/forms';

import { safeCompare, trimLeadingZeros } from '@shared-helpers/string-number-helpers';

import { IAnyCatsType, ITableConfigEntity } from '../../../interfaces/internal-interfaces';
import { ExtDocument } from '../../../scripts/extDocument';
import { ProductionGroup } from '../../../scripts/Production-Group/ProductionGroup';
import { ProductionTextRecord } from '../../../scripts/ProductionTextRecord';
import { ITimesheetHeader, TimesheetHelperService } from '../../../services/timesheet-helper.service';

export interface IModalContentCreateOrEditTimesheetResult {
  headers: ITimesheetHeader[];
  description: string;
}

@Component({
  selector: 'app-modal-content-timesheet-create-or-edit',
  templateUrl: './modal-content-timesheet-create-or-edit.component.html',
  standalone: false,
})
export class ModalContentTimesheetCreateComponent implements OnInit {
  @Output() public readonly closed = new EventEmitter<IModalContentCreateOrEditTimesheetResult>();
  @Input() public record?: IAnyCatsType; //this can be optionally provided to pre-populate the timesheet being created
  @Input() public productionGroup?: ProductionGroup; //this can be optionally provided to pre-populate the timesheet being created
  @Input() public allExtDocuments?: ExtDocument[];
  @Input() public applicableExtDocuments?: ExtDocument[];
  @Input() public headerConfigs?: ITableConfigEntity<keyof ExtDocument>[];
  @Input() public existingDescription?: string; //If we are passed an existing description, then we are editing and not creating
  @Input() public selectedCostCenterCondGrpTabs: { costCenter: string; custGroup: string };

  public isEditing = false;
  public duplicateTimesheetNumber = '';
  public activityName = '';
  public headersList: ITimesheetHeader[] = [];
  public descriptionCtrl = new FormControl<string>('', {
    validators: [Validators.required, this.noDuplicateDescriptionValidator(), this.minLengthNoWhitespaceValidator()],
    updateOn: 'change',
  });

  public get isQuantity(): boolean {
    return this.record && (this.record.__type === 'pqt' || this.record.__type === 'peq');
  }

  public get isText(): boolean {
    return this.record && this.record.__type === 'ptx';
  }

  public get textDisplayName(): string {
    //This is only called in the the template after we have determined this is a ProductionText record type, so this is safe to force the type here
    return this.record ? (this.record as ProductionTextRecord).parent.display : '';
  }

  public get textDisplayDay(): string {
    //This is only called in the the template after we have determined this is a ProductionText record type, so this is safe to force the type here
    return this.record ? (this.record as ProductionTextRecord).parent.focusedDay.moment.format('dddd') : '';
  }

  public get descriptionMinLength(): number {
    return this.timesheetHelperSvc.timesheetDescriptionMinLength;
  }

  constructor(private readonly timesheetHelperSvc: TimesheetHelperService) {}

  public ngOnInit(): void {
    if (this.record && this.productionGroup) {
      throw Error('You cannot provide BOTH a record AND a productionGroup, it must be one or the other!');
    }

    if (typeof this.existingDescription === 'string') {
      this.isEditing = true;
      this.descriptionCtrl.setValue(this.existingDescription);
    }

    this.headersList = this.getHeadersAndOptions();

    if (this.record != null) {
      this.activityName = this.timesheetHelperSvc.getActivityDescription(this.record);
    }
  }

  public trimWhitespace(): void {
    this.descriptionCtrl.setValue(this.descriptionCtrl.value.trim());
  }

  public handleHeaderSelectionChange(changedHeader: ITimesheetHeader, updatedValue: string): void {
    changedHeader.selectedValue = updatedValue;
  }

  public handleSubmitClicked(): void {
    if (this.descriptionCtrl.valid) {
      //close the modal
      this.closed.emit({
        headers: this.headersList,
        description: this.descriptionCtrl.value.trim(), //always send out the trimmed version, we never want extra spaces
      });
    }
  }

  private noDuplicateDescriptionValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const normalizedValue = (control.value ?? '').toLowerCase().trim();
      const duplicateDocument = this.allExtDocuments?.find(
        //Ignore leading/trailing whitespace and casing
        (doc) => doc.Description.toLowerCase().trim() === normalizedValue,
      );
      let hasDuplicate = duplicateDocument != null;

      if (duplicateDocument) {
        this.descriptionCtrl.markAllAsTouched();
        this.duplicateTimesheetNumber = trimLeadingZeros(duplicateDocument.Documentno ?? '');
      }

      //If editing, we shouldn't treat the original description as a duplicate if it is unchanged
      if (this.isEditing && normalizedValue === this.existingDescription.toLowerCase().trim()) hasDuplicate = false;

      return hasDuplicate ? { duplicateDescription: true } : null;
    };
  }

  private minLengthNoWhitespaceValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      //validates a minimum length after trimming out whitespace from the start and end
      const trimmedVal = (control.value ?? '').trim();
      const isValidLength = trimmedVal.length >= this.descriptionMinLength;
      return isValidLength ? null : { minlengthNoWhitespace: true };
    };
  }

  private getHeadersAndOptions(currentDocumentNumber?: string): ITimesheetHeader[] {
    const headers: ITimesheetHeader[] = [];

    if (this.headerConfigs?.length > 0) {
      const headersFromConfig = this.headerConfigs.filter((config) => config.key.toLowerCase().includes('genericfld'));

      headersFromConfig.forEach((config) => {
        config.shouldRemoveMatStyling = false;

        headers.push({ config, selectedValue: undefined });
      });
    } else if (this.applicableExtDocuments?.length > 0) {
      let relevantDocument: ExtDocument | undefined;

      if (typeof currentDocumentNumber === 'string') {
        //We only need to grab the headers off of the passed in document
        relevantDocument = this.applicableExtDocuments.find((doc) => safeCompare(doc.Documentno, currentDocumentNumber));
      }

      if (relevantDocument == null) {
        //If no document was passed in or we can't locate it, then use the first document
        relevantDocument = this.applicableExtDocuments[0];
      }
      relevantDocument.headerConfig.forEach((config) => {
        const key = config.key;
        const val = relevantDocument[key];
        config.shouldRemoveMatStyling = false;

        //If a document number was passed in, then grab the value from one of the `Genericfld**` properties on the ExtDocument
        //If not, then there is no selected value
        const selectedValue = currentDocumentNumber != null && typeof val === 'string' ? val : undefined;

        headers.push({ config, selectedValue });
      });
    }

    return headers;
  }
}
